import { css } from 'lit'
import breakpoints from './breakpoints.js'

export default css`
  :root {
    --hh-font-family-primary: 'FS Industrie', sans-serif;
    --hh-font-family-secondary: 'FS Industrie', sans-serif;
    --hh-font-family-tertiary: 'InputMonoCondensed', sans-serif;

    /* Colors */
    --hh-color-black: #000000;
    --hh-color-aged-black: #2b2829;
    --hh-color-ancient-ore: #6a6868;
    --hh-color-unrefined-silver: #8b8882;
    --hh-color-grey-mineral: #cdcbca;
    --hh-color-sundrenched-stone: #ebe8e5;
    --hh-color-star-white: #faf9f8;
    --hh-color-fermented-honey: #815D36;
    --hh-color-honey-two: #996726;
    --hh-color-danger: #D40000;
    --hh-color-success: #25B900;
    --hh-color-overlay: rgba(43, 40, 41, 0.1);
    --hh-okendo-answer-bg: rgba(250, 249, 248, 0.5);
    --hh-active-option-bg: #e0dedb;

    --hh-color-focus: #0038FF;
    --hh-color-text: var(--hh-color-aged-black);
    --hh-color-light: var(--hh-color-star-white);

    --screen-size: 375;
    --hh-font-h1: 28;
    --hh-font-h2: 16;
    --hh-font-h3: 14;
    --hh-font-h4: 14;
    --hh-font-h5: 28;
    --hh-font-h6: 24;
    --hh-font-body-large: 24;
    --hh-font-body: 14;
    --hh-font-caption: 11;
    --hh-font-utility: 14;
    --hh-font-pullquote: 24;
    --hh-font-bullet-list: 14;
    --hh-font-inline-link: 14;

    --hh-font-size-h1: calc(var(--hh-font-h1) * 1px);
    --hh-font-size-h2: calc(var(--hh-font-h2) * 1px);
    --hh-font-size-h3: calc(var(--hh-font-h3) * 1px);
    --hh-font-size-h4: calc(var(--hh-font-h4) * 1px);
    --hh-font-size-h5: calc(var(--hh-font-h5) * 1px);
    --hh-font-size-h6: calc(var(--hh-font-h6) * 1px);
    --hh-font-size-body-large: calc(var(--hh-font-body-large) * 1px);
    --hh-font-size-body: calc(var(--hh-font-body) * 1px);
    --hh-font-size-caption: calc(var(--hh-font-caption) * 1px);
    --hh-font-size-utility: calc(var(--hh-font-utility) * 1px);
    --hh-font-size-pullquote: calc(var(--hh-font-pullquote) * 1px);
    --hh-font-size-bullet-list: calc(var(--hh-font-bullet-list) * 1px);
    --hh-font-size-inline-link: calc(var(--hh-font-inline-link) * 1px);

    /* Typography */
    --hh-h1-font: 400 var(--hh-font-size-h1) / 1.28571 var(--hh-font-family-primary);
    --hh-h2-font: 400 var(--hh-font-size-h2) / 1.5 var(--hh-font-family-tertiary);
    --hh-h3-font: 400 var(--hh-font-size-h3) / 1.4 var(--hh-font-family-tertiary);
    --hh-h4-font: 400 var(--hh-font-size-h4) / 1.42857 var(--hh-font-family-tertiary);
    --hh-h5-font: 400 var(--hh-font-size-h5) / 1.28571 var(--hh-font-family-primary);
    --hh-h6-font: 400 var(--hh-font-size-h6) / 1.33333 var(--hh-font-family-tertiary);
    --hh-body-font-large: 400 var(--hh-font-size-body-large) / 1.33333 var(--hh-font-family-primary);
    --hh-body-font: 400 var(--hh-font-size-body) / 1.71429 var(--hh-font-family-primary);
    --hh-body-caption: 400 var(--hh-font-size-caption) / 1.4 var(--hh-font-family-primary);
    --hh-body-utility: 500 var(--hh-font-size-utility) / 1.42857 var(--hh-font-family-tertiary);
    --hh-body-pullquote: 400 var(--hh-font-size-pullquote) / 1 var(--hh-font-family-primary);
    --hh-body-bullet-list: 400 var(--hh-font-size-bullet-list) / 1.71429 var(--hh-font-family-primary);
    --hh-body-inline-link: 400 var(--hh-font-size-inline-link) / 1.4 var(--hh-font-family-primary);

    --hh-h1-letter-spacing: normal;
    --hh-h2-letter-spacing: normal;
    --hh-h3-letter-spacing: -0.42px;
    --hh-h4-letter-spacing: normal;
    --hh-h5-letter-spacing: normal;
    --hh-h6-letter-spacing: normal;
    --hh-body-letter-spacing: normal;
    --hh-caption-letter-spacing: normal;
    --hh-utility-letter-spacing: -0.36px;
    --hh-inline-link-letter-spacing: -0.42px;

    --hh-h1-text-transform: uppercase;
    --hh-h2-text-transform: uppercase;
    --hh-h3-text-transform: uppercase;
    --hh-h4-text-transform: uppercase;
    --hh-h5-text-transform: none;
    --hh-h6-text-transform: none;
    --hh-body-text-transform: none;
    --hh-caption-text-transform: none;
    --hh-utility-text-transform: uppercase;

    --hh-text-decoration-underline: underline;

    /* Transitions */
    --hh-duration-short: 100ms;
    --hh-duration-default: 200ms;
    --hh-duration-long: 500ms;
    --hh-duration-extra-long: 2s;

    /* A18n */
    --hh-focus-outline-offset: 0;

    /* Spacing */
    --hh-section-vertical-margin: 5rem;

    --hh-spacing-1: 10px;
    --hh-spacing-2: 20px;
    --hh-spacing-3: 30px;
    --hh-spacing-4: 40px;
    --hh-spacing-5: 50px;
    --hh-spacing-6: 60px;
    --hh-spacing-7: 70px;
    --hh-spacing-8: 80px;
    --hh-spacing-9: 90px;
    --hh-spacing-10: 100px;

    --hh-grid-gutter: 0.5rem;

    /* z-index */
    --hh-z-base: 1;
    --hh-z-below: -1;
    --hh-z-header: 99;
    --hh-z-modal: 100;
    --hh-z-highest: 9999;

    /* Gutters & Containers */
    --gutter: 2rem;
    --br-height: 1.5rem;
    --container-width: 144rem;
    --header-spacing: 13.3rem;

    /* Focal Point - Padding */
    --focal-point-padding: 0;

    /* Button Max Width - Mobile(sm + md) */
    --button-max-width-mobile: 40rem;
  }


  /* breakpoints.md */
  @media screen and (min-width: ${breakpoints.md}) {
    :root {
      --screen-size: 1920;
      --hh-font-h1: 40;
      --hh-font-h2: 20;
      --hh-font-h5: 40;
      --hh-font-h6: 32;
      --hh-font-caption: 12;

      --pixels-to-vw-h1: calc(var(--hh-font-h1) / var(--screen-size) * 100vw);
      --pixels-to-vw-h2: calc(var(--hh-font-h2) / var(--screen-size) * 100vw);
      --pixels-to-vw-h3: calc(var(--hh-font-h3) / var(--screen-size) * 100vw);
      --pixels-to-vw-h4: calc(var(--hh-font-h4) / var(--screen-size) * 100vw);
      --pixels-to-vw-h5: calc(var(--hh-font-h5) / var(--screen-size) * 100vw);
      --pixels-to-vw-h6: calc(var(--hh-font-h6) / var(--screen-size) * 100vw);
      --pixels-to-vw-body-large: calc(var(--hh-font-body-large) / var(--screen-size) * 100vw);
      --pixels-to-vw-body: calc(var(--hh-font-body) / var(--screen-size) * 100vw);
      --pixels-to-vw-caption: calc(var(--hh-font-caption) / var(--screen-size) * 100vw);
      --pixels-to-vw-utility: calc(var(--hh-font-utility) / var(--screen-size) * 100vw);
      --pixels-to-vw-pullquote: calc(var(--hh-font-pullquote) / var(--screen-size) * 100vw);
      --pixels-to-vw-bullet-list: calc(var(--hh-font-bullet-list) / var(--screen-size) * 100vw);
      --pixels-to-vw-inline-link: calc(var(--hh-font-inline-link) / var(--screen-size) * 100vw);

      --hh-font-size-h1: max(var(--pixels-to-vw-h1), calc(var(--hh-font-h1) * 1px));
      --hh-font-size-h2: max(var(--pixels-to-vw-h2), calc(var(--hh-font-h2) * 1px));
      --hh-font-size-h3: max(var(--pixels-to-vw-h3), calc(var(--hh-font-h3) * 1px));
      --hh-font-size-h4: max(var(--pixels-to-vw-h4), calc(var(--hh-font-h4) * 1px));
      --hh-font-size-h5: max(var(--pixels-to-vw-h5), calc(var(--hh-font-h5) * 1px));
      --hh-font-size-h6: max(var(--pixels-to-vw-h6), calc(var(--hh-font-h6) * 1px));
      --hh-font-size-body-large: max(var(--pixels-to-vw-body-large), calc(var(--hh-font-body-large) * 1px));
      --hh-font-size-body: max(var(--pixels-to-vw-body), calc(var(--hh-font-body) * 1px));
      --hh-font-size-caption: max(var(--pixels-to-vw-caption), calc(var(--hh-font-caption) * 1px));
      --hh-font-size-utility: max(var(--pixels-to-vw-utility), calc(var(--hh-font-utility) * 1px));
      --hh-font-size-pullquote: max(var(--pixels-to-vw-pullquote), calc(var(--hh-font-pullquote) * 1px));
      --hh-font-size-bullet-list: max(var(--pixels-to-vw-bullet-list), calc(var(--hh-font-bullet-list) * 1px));
      --hh-font-size-inline-link: max(var(--pixels-to-vw-inline-link), calc(var(--hh-font-inline-link) * 1px));

      --hh-h1-font: 400 var(--hh-font-size-h1) / 1.2 var(--hh-font-family-primary);
      --hh-h2-font: 400 var(--hh-font-size-h2) / 1.4 var(--hh-font-family-tertiary);
      --hh-h3-font: 400 var(--hh-font-size-h3) / 1.4 var(--hh-font-family-tertiary);
      --hh-h4-font: 400 var(--hh-font-size-h4) / 1.4 var(--hh-font-family-tertiary);
      --hh-h5-font: 400 var(--hh-font-size-h5) / 1.2 var(--hh-font-family-primary);
      --hh-h6-font: 400 var(--hh-font-size-h6) / 1.25 var(--hh-font-family-tertiary);
      --hh-body-font-large: 400 var(--hh-font-size-body-large) / 1.33333 var(--hh-font-family-primary);
      --hh-body-font: 400 var(--hh-font-size-body) / 1.71429 var(--hh-font-family-primary);
      --hh-body-caption: 400 var(--hh-font-size-caption) / 1.5 var(--hh-font-family-primary);
      --hh-body-utility: 500 var(--hh-font-size-utility) / 1.42857 var(--hh-font-family-tertiary);
      --hh-body-pullquote: 400 var(--hh-font-size-pullquote) / 1 var(--hh-font-family-primary);
      --hh-body-bullet-list: 400 var(--hh-font-size-bullet-list) / 1.71429 var(--hh-font-family-primary);
      --hh-body-inline-link: 400 var(--hh-font-size-inline-link) / 1.4 var(--hh-font-family-primary);

      --hh-body-letter-spacing: -0.42px;
      --hh-caption-letter-spacing: -0.36px;
      --hh-utility-letter-spacing: -0.42px;

      --hh-grid-gutter: 1rem;

      --gutter: 6rem;

      /* Focal Point - Padding */
      --focal-point-padding: 4.3rem;
    }
  }
`
